import { Injectable } from '@angular/core';
import FroalaEditor from 'froala-editor';

@Injectable({
    providedIn: 'root',
})
export class FroalaActionsService {
    readonly #fieldMergeRemoveHandlers = new WeakMap<Element, EventListener>();

    isDragStartWithinEditor(dropEvent: any): boolean {
        let data;
        try {
            data = dropEvent.dataTransfer.getData('is-drag-within');
        } catch {
            data = dropEvent.originalEvent.dataTransfer.getData('is-drag-within');
        }
        return data === 'true';
    }

    getHtmlData(dropEvent: any): string {
        try {
            return dropEvent.dataTransfer.getData('field-merge-html');
        } catch {
            return dropEvent.originalEvent.dataTransfer.getData('field-merge-html');
        }
    }

    createNode(nodeString: string): DocumentFragment {
        const node: DocumentFragment = document.createRange().createContextualFragment(nodeString);
        const childNode: Element = node.childNodes[0] as Element;
        // browser quirks
        node.childNodes.forEach(child => {
            if (child.nodeName === 'META') {
                node.removeChild(child);
            }
        });
        const removeButton = document.createElement('button');
        removeButton.type = 'button';
        const removeButtonText = document.createTextNode('x');
        removeButton.classList.add('field-merge-remove');
        if (childNode.classList.contains('tfs-field-merge')) {
            removeButton.classList.add('tfs-remove');
        } else if (childNode.classList.contains('tfs-pipedelimited-field-merge')) {
            removeButton.classList.add('tfs-pipedelimited-remove');
        } else if (childNode.classList.contains('dynamic-content-field-merge')) {
            removeButton.classList.add('dynamic-content-field-remove');
        } else if (childNode.classList.contains('cdo-field-merge')) {
            removeButton.classList.add('cdo-field-remove');
        } else if (childNode.classList.contains('json-field-merge')) {
            removeButton.classList.add('json-field-remove');
        } else if (childNode.classList.contains('json-array-field-merge')) {
            removeButton.classList.add('json-array-field-remove');
        }
        removeButton.appendChild(removeButtonText);

        node.childNodes.forEach(child => {
            if (child.nodeName === 'SPAN' || child.nodeName === 'DIV') {
                child.appendChild(removeButton);
                child.after(document.createTextNode('\u00A0'));
            }
        });
        return node;
    }

    insertNode(target: HTMLElement, targetClassList: DOMTokenList, node: DocumentFragment) {
        if (target) {
            if (targetClassList.contains('field-merge') || targetClassList.contains('field-merge-remove')) {
                target.closest('.field-merge')?.after(node);
            } else {
                target.replaceWith(node);
            }
        }
    }

    showCursorForFirefox(editor: FroalaEditor) {
        editor.events.disableBlur();
        setTimeout(function () {
            editor.$el.blur().focus();
            editor.events.enableBlur();
        }, 0);
    }

    setDataOnDragEvent(dragEvent: any) {
        const dataTransferData = dragEvent.originalEvent.target.outerHTML;
        dragEvent.originalEvent.dataTransfer.setData('field-merge-html', dataTransferData);
        dragEvent.originalEvent.dataTransfer.setData('is-drag-within', 'true');
        try {
            dragEvent.originalEvent.dataTransfer.setData('text/plain', '');
        } catch (e) {
            dragEvent.originalEvent.dataTransfer.setData('text', '');
        }
    }

    adjustNodeSpacing() {
        const fieldMerges = document.querySelectorAll('.fr-element .field-merge');
        fieldMerges.forEach(fieldMerge => {
            this.removeTextNodesOnRight(fieldMerge.nextSibling, fieldMerge);
            this.removeTextNodesOnLeft(fieldMerge.previousSibling);
        });
        fieldMerges.forEach(fieldMerge => {
            fieldMerge.after(document.createTextNode('\u00A0'));
        });
    }

    adjustSingleNodeSpacing(nodeToUpdate: HTMLElement) {
        const nodeValue = nodeToUpdate.nextSibling?.nodeValue;
        if (nodeValue?.charCodeAt(0) !== 32 && nodeValue?.charCodeAt(0) !== 160) {
            nodeToUpdate.after(document.createTextNode('\u00A0'));
        }
    }

    removeTextNodesOnRight(node: any, fieldMerge: any) {
        if (node && node.nodeName === '#text' && node.nodeType === 3) {
            const nextNode = node.nextSibling;
            if (node.nodeValue?.trim().length === 0) {
                node.remove();
                this.removeTextNodesOnRight(nextNode, fieldMerge);
            } else {
                const trimmedNode = node.nodeValue.trim(1);
                node.remove();
                fieldMerge.after(document.createTextNode(trimmedNode));
                this.removeTextNodesOnRight(nextNode, fieldMerge);
            }
        }
    }

    removeTextNodesOnLeft(node: any) {
        if (node && node.nodeName === '#text' && node.nodeType === 3) {
            if (node.nodeValue.trim().length === 0) {
                const previousNode = node.previousSibling;
                node.remove();
                this.removeTextNodesOnLeft(previousNode);
            }
        }
    }

    cancelEvent(event: DragEvent): boolean {
        if (event.preventDefault) {
            event.preventDefault();
        }
        if (event.stopPropagation) {
            event.stopPropagation();
        }
        return false;
    }

    addEventListenersToEditorContainer(dropped: any, dragEnter: any, dragOver: any) {
        const dropTargets = document.querySelectorAll('[data-role="drag-drop-target"] div.fr-view'); //there could be multiple editors in one app
        dropTargets.forEach(dropTarget => {
            dropTarget.addEventListener('drop', dropped, false);
            dropTarget.addEventListener('dragenter', dragEnter, false);
            dropTarget.addEventListener('dragover', dragOver, false);
        });
    }

    addClickEventToFieldMergeRemoveButtons(
        onFieldMergeDeleted?: (event: MouseEvent, fieldMergeElement: HTMLElement) => void,
        root: ParentNode = document,
        editor?: FroalaEditor
    ) {
        const fieldMergeRemoveButtons = root.querySelectorAll('.field-merge-remove');
        fieldMergeRemoveButtons.forEach(button => {
            const existingHandler = this.#fieldMergeRemoveHandlers.get(button);
            if (existingHandler && !onFieldMergeDeleted) {
                return;
            }
            if (existingHandler) {
                button.removeEventListener('click', existingHandler, false);
            }

            const removeHandler: EventListener = event => {
                event.preventDefault();
                event.stopPropagation();
                const mouseEvent = event as MouseEvent;
                const fieldMergeElement = this.removeFieldMergeFromEditor(mouseEvent, editor);
                if (fieldMergeElement) {
                    onFieldMergeDeleted?.(mouseEvent, fieldMergeElement);
                }
            };
            this.#fieldMergeRemoveHandlers.set(button, removeHandler);
            button.addEventListener('click', removeHandler, false);
        });
    }

    removeFieldMergeFromEditor(event: MouseEvent, editor?: FroalaEditor): HTMLElement | undefined {
        const removeButton = event.currentTarget as HTMLElement | null;
        const fieldMergeElement = removeButton?.closest<HTMLElement>('.field-merge');
        if (!fieldMergeElement) {
            return undefined;
        }

        const editorElement = fieldMergeElement.closest<HTMLElement>('.fr-element');
        const prevSibling = fieldMergeElement.previousSibling;
        const nextSibling = fieldMergeElement.nextSibling;

        fieldMergeElement.remove();

        // Prevent double spaces between adjacent words
        if (prevSibling instanceof Text && nextSibling instanceof Text) {
            const hasTrailingSpace = prevSibling.data !== prevSibling.data.trimEnd();
            const hasLeadingSpace = nextSibling.data !== nextSibling.data.trimStart();

            if (hasTrailingSpace && hasLeadingSpace) {
                nextSibling.data = nextSibling.data.trimStart();
            }
        }

        // Merge adjacent text nodes back together
        editorElement?.normalize();

        // If no meaningful text or media elements remain, clear the editor to trip validation
        const hasContent =
            Boolean(editorElement?.textContent?.trim()) ||
            Boolean(editorElement?.querySelector('img, table, iframe, hr, .field-merge'));

        if (!hasContent) {
            editor?.html.set('');
        }

        if (editor) {
            editor.undo.saveStep();
            editor.events.trigger('contentChanged', []);
        }

        editorElement?.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }));
        editorElement?.focus();

        return fieldMergeElement;
    }
}
